import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LogIn, User, Menu, Heart, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useFavorites } from '../context/FavoritesContext';
import { useNotifications } from '../context/NotificationContext';
import { SideMenu } from './SideMenu';

/**
 * DesktopTopNav — horizontal top navigation, visible only on desktop (md+).
 *
 * Mobile: completely hidden via `hidden md:block`. The mobile UI continues
 * to use BottomNav + per-page PageHeader (with menu drawer) unchanged.
 *
 * Desktop: persistent strip across all pages with logo, three primary tabs
 * (მთავარი / ძებნა / ფასდაკლებები) and a login/account slot on the right.
 *
 * The nav hides itself on /auth and /admin/* routes — those pages are
 * standalone surfaces where a global nav above them would be noise.
 */
export function DesktopTopNav() {
  const { isAuthenticated, user } = useAuth();
  const { settings } = useSiteSettings();
  const { pathname } = useLocation();
  const { favorites } = useFavorites();
  const { unreadCount, totalUnread } = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const favCount = favorites.length;

  // Hide on auth + admin surfaces.
  if (pathname === '/auth' || pathname === '/pm-admin' || pathname.startsWith('/admin')) {
    return null;
  }

  const showLogin = settings?.show_login_button !== false;

  return (
    <>
    <header
      className="hidden md:block sticky top-0 z-50 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border-b border-slate-100"
      data-testid="desktop-top-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-6">
          {/* Logo + wordmark */}
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 active:scale-95 transition-transform"
            data-testid="desktop-nav-logo"
          >
            <img
              src="/pricemap-logo.svg"
              alt=""
              className="w-8 h-8"
              draggable="false"
            />
            <span className="text-[20px] font-bold text-slate-900 tracking-tight">
              PriceMap
            </span>
          </Link>

          {/* Primary nav links */}
          <nav className="flex items-center gap-1">
            <NavTab to="/" end>ფასდაკლებები</NavTab>
            <NavTab to="/search">ფასები</NavTab>
            <NavTab to="/discounts">მთავარი</NavTab>
          </nav>

          {/* Right slot — favorites, notifications, auth, menu */}
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/favorites"
              className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
              data-testid="desktop-nav-fav"
              aria-label="ფავორიტები"
            >
              <Heart className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
              {favCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 ring-2 ring-white flex items-center justify-center px-1 text-[9px] font-bold text-white leading-none">
                  {favCount > 99 ? '99+' : favCount}
                </span>
              )}
            </Link>

            <Link
              to="/notifications"
              className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
              data-testid="desktop-nav-notifications"
              aria-label="შეტყობინებები"
            >
              <Bell className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-red-500 ring-2 ring-white flex items-center justify-center px-1 text-[9px] font-bold text-white leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>

            {isAuthenticated ? (
              /* [2026-06-12] Account pill now opens the SideMenu (the old
                 /settings link pointed to a route that never existed →
                 blank page). The hamburger is removed for authenticated
                 users — this pill IS the menu button, so it also carries
                 the unread red dot. */
              <button
                onClick={() => setMenuOpen(true)}
                className="relative flex items-center gap-2 px-3 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 transition-all"
                data-testid="desktop-nav-account"
                aria-label="მენიუ"
              >
                <User className="w-4 h-4 text-slate-600" strokeWidth={1.7} />
                <span className="text-sm font-medium text-slate-700 max-w-[120px] truncate">
                  {user?.name || 'ანგარიში'}
                </span>
                {totalUnread > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
                )}
              </button>
            ) : showLogin ? (
              <Link
                to="/auth"
                state={{ from: 'nav' }}
                className="flex items-center gap-1.5 px-4 h-10 rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-sm font-medium transition-all"
                data-testid="desktop-nav-login"
              >
                <LogIn className="w-4 h-4" strokeWidth={1.8} />
                შესვლა
              </Link>
            ) : null}

            {/* Hamburger stays ONLY for anonymous visitors — they have no
                account pill, and removing it entirely would cut off menu
                access (პარამეტრები, შესვლა/რეგისტრაცია). */}
            {!isAuthenticated && (
            <button
              onClick={() => setMenuOpen(true)}
              className="relative w-10 h-10 rounded-full bg-slate-50 hover:bg-slate-100 active:scale-95 flex items-center justify-center transition-all"
              data-testid="desktop-nav-menu"
              aria-label="მენიუ"
            >
              <Menu className="w-5 h-5 text-slate-600" strokeWidth={1.7} />
            </button>
            )}
          </div>
        </div>
      </div>
    </header>
    <SideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
}

/* ─── helper ──────────────────────────────────────────────── */

function NavTab({ to, end, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `px-4 py-2 rounded-lg text-[14px] font-medium transition-colors ${
          isActive
            ? 'bg-blue-50 text-blue-600'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export default DesktopTopNav;
